import { Router, ActivatedRoute, Params } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FilterPersistenceService } from '../services/filter-persistence.service';

export interface FilterMapper<T> {
  toQueryParams(filters: T): Record<string, string | undefined>;
  fromQueryParams(queryParams: Params): T;
}

export class FilterPersistenceManager<T extends Record<string, unknown>> {
  private filtersRestored = false;
  private lastNavigatedParams: Params = {};

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly filterPersistence: FilterPersistenceService,
    private readonly mapper: FilterMapper<T>,
    private readonly storageKey: string,
    private readonly routePath: string
  ) {}

  isFiltersRestored(): boolean {
    return this.filtersRestored;
  }

  setFiltersRestored(value: boolean): void {
    this.filtersRestored = value;
  }

  getLastNavigatedParams(): Params {
    return this.lastNavigatedParams;
  }

  updateLastNavigatedParams(params: Params): void {
    this.lastNavigatedParams = params;
  }

  hasParamsChanged(params: Params): boolean {
    const paramStr = JSON.stringify(params);
    const lastStr = JSON.stringify(this.lastNavigatedParams);
    return paramStr !== lastStr;
  }

  shouldRestoreFromLocalStorage(params: Params): boolean {
    return Object.keys(params).length === 0 && this.filtersRestored;
  }

  restoreFilters(snapshot: Params, fallback: T): T {
    if (Object.keys(snapshot).length > 0) {
      return this.mapper.fromQueryParams(snapshot);
    }
    return this.restoreFromLocalStorage(fallback);
  }

  restoreFromLocalStorage(fallback: T): T {
    if (this.filtersRestored) return fallback;

    const stored = this.filterPersistence.loadFilters(this.storageKey);
    if (!stored || typeof stored !== 'object') return fallback;

    return this.validateAndMergeFilters(stored, fallback);
  }

  private validateAndMergeFilters(stored: unknown, fallback: T): T {
    const merged = { ...fallback };
    if (typeof stored === 'object' && stored !== null) {
      Object.assign(merged, stored);
    }
    return merged;
  }

  saveFilters(filters: T): void {
    try {
      this.filterPersistence.saveFilters(this.storageKey, filters);
    } catch (error) {
      console.error(`Failed to save ${this.storageKey} to localStorage`, error);
    }
  }

  updateQueryParams(filters: T): void {
    const queryParams = this.mapper.toQueryParams(filters);

    this.router.navigate([this.routePath], {
      queryParams: queryParams,
      replaceUrl: true,
    });
  }

  clearFilters(fallback: T): void {
    try {
      this.filterPersistence.clearFilters(this.storageKey);
    } catch (error) {
      console.error(
        `Failed to clear ${this.storageKey} from localStorage`,
        error
      );
    }

    this.updateQueryParams(fallback);
  }

  monitorQueryParamsChanges(
    destroy$: Subject<void>,
    onFiltersRestored: (filters: T) => void,
    defaultFilters: T
  ): void {
    this.route.queryParams.pipe(takeUntil(destroy$)).subscribe(params => {
      if (!this.hasParamsChanged(params)) return;

      this.updateLastNavigatedParams(params);

      if (this.shouldRestoreFromLocalStorage(params)) {
        const restored = this.restoreFromLocalStorage(defaultFilters);
        onFiltersRestored(restored);
      }
    });
  }
}
